import type { PublicUser } from '@flame/shared';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { api } from '../api';

type Options = { ceremonyId: string; options: any };

/** A best-guess name for the passkey being created on this device. */
export function deviceName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : '';
  return browser ? `${os} · ${browser}` : os;
}

/** Runs options → browser passkey prompt → verify for a registration endpoint pair. */
export async function registerPasskey<T>(
  optionsPath: string,
  verifyPath: string,
  optionsBody: Record<string, unknown> = {},
  nameField: 'passkeyName' | 'name' = 'passkeyName'
): Promise<T> {
  const { ceremonyId, options } = await api<Options>('POST', optionsPath, optionsBody);
  const response = await startRegistration({ optionsJSON: options });
  return api<T>('POST', verifyPath, { ceremonyId, response, [nameField]: deviceName() });
}

export async function signInWithPasskey(): Promise<PublicUser> {
  const { ceremonyId, options } = await api<Options>('POST', '/api/auth/login/options');
  const response = await startAuthentication({ optionsJSON: options });
  const { user } = await api<{ user: PublicUser }>('POST', '/api/auth/login/verify', {
    ceremonyId,
    response,
  });
  return user;
}
